import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {DetailsField} from '@/components/ui/details-atoms/DetailsAtoms'
import {DialogBody} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {Text} from '@/components/ui/text/Text'
import {formatDate, formatLocaleDate} from '@/dayjs'
import {AgendaEventProjectInfo} from '@/features/agenda/components/agenda-event-project-info/AgendaEventProjectInfo'
import {EventRepetitionFrequencyText} from '@/features/agenda/components/event-repetition-frequency-text/EventRepetitionFrequencyText'
import {QuickEditEventFormValidationSchema} from '@/features/agenda/formSchemas'
import {AgendaEventConsultant, EventExtended} from '@/features/agenda/types'
import {eventTypeMapToLabel, locationMapToLabel} from '@/features/agenda/utils'
import {useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {useFormContext, useWatch} from 'react-hook-form'
import {generatePath} from 'react-router-dom'
import {useMemo} from 'react'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {useTheme} from 'styled-components'
import {Badge} from '@/components/ui/badge/Badge'
import {useAuthStore, usePermissions} from '@/features/auth/store/store'
import {ProjectType} from '@/features/projects/types'
import {AgendaSubstituteField} from '@/features/agenda/components/agenda-event-form/agenda-substitute-field/AgendaSubstituteField'

export const AgendaEventDetailsDialogBody = ({event}: {event: EventExtended}) => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const authUser = useAuthStore(state => state.user)
    const isTherapistNotAssignedToEvent =
        !!authUser?.roleName?.includes('Terapista') &&
        !event.eventConsultants?.some(consultant => consultant.userId == authUser.id)
    const form = useFormContext<QuickEditEventFormValidationSchema>()
    const substituteWatch = useWatch({control: form.control, name: 'substitute'})
    const defaultStatus = form.formState.defaultValues?.status
    const {palette} = useTheme()

    // Get the first validation error from form state
    const firstValidationError = useMemo(() => {
        const errors = form.formState.errors
        if (!errors) return null

        // Check for errors in consultants array (access type validation)
        if (errors.consultants && Array.isArray(errors.consultants)) {
            for (const consultantError of errors.consultants) {
                if (consultantError?.accessType?.message) {
                    return t('agenda:incompatible_access_type_subtitle')
                }
            }
        }

        return null
    }, [form.formState.errors])

    const renderAccessType = (
        accessType?: AgendaEventConsultant['accessType'] | null,
        projectType?: ProjectType | null
    ) => {
        if (!accessType) return '-'

        if (accessType.accessType == 'project_opening_processing' && projectType == 'cyclic') {
            return (
                <Flexbox gap={2}>
                    <Text size="textSm" weight="500" style={{textDecoration: 'line-through'}}>
                        {t(eventTypeMapToLabel[accessType.accessType])}
                    </Text>
                    <Text size="textSm" weight="500">
                        {t(eventTypeMapToLabel['cycle_opening_processing'])}
                    </Text>
                </Flexbox>
            )
        }

        return (
            <Text size="textSm" weight="500">
                {t(eventTypeMapToLabel[accessType.accessType])}
            </Text>
        )
    }

    return (
        <DialogBody>
            {event.eventPatients?.map(ep => (
                <Grid columns={2} key={ep.patientId}>
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                            {t('commons:patient')}
                        </Text>
                        {permissions?.patients?.read && !isTherapistNotAssignedToEvent ? (
                            <ButtonLink
                                to={generatePath(routes.PATIENT_DETAILS_ROUTE.handle.path, {id: ep.patientId})}
                                shape="text"
                                size="xs"
                                variant="tertiary"
                                target="_blank"
                            >
                                <Text size="textSm" weight="500">
                                    {ep.patient?.fullName}
                                </Text>
                            </ButtonLink>
                        ) : (
                            <Text size="textSm" weight="500">
                                {ep.patient?.fullName}
                            </Text>
                        )}
                    </DetailsField>
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                            {t('commons:pri')}
                        </Text>
                        {ep.project ? (
                            <AgendaEventProjectInfo
                                patientName={ep.patient?.fullName || ''}
                                project={ep.project}
                                disableNavigation={isTherapistNotAssignedToEvent}
                            />
                        ) : (
                            <Text weight="400" palette="neutral" shade="500" size="textSm">
                                {t('agenda:no_project_found', {name: ep.patient?.fullName})}
                            </Text>
                        )}
                    </DetailsField>
                </Grid>
            ))}
            <Divider direction="horizontal" />
            {event.eventConsultants?.map(ec => (
                <Grid columns={2} key={ec.userId} gap={4}>
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                            {t('commons:access_type')}
                        </Text>
                        {renderAccessType(ec.accessType, event.eventPatients?.[0]?.project?.type)}
                    </DetailsField>
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                            {t('commons:healthcare_professional', {count: 1})}
                        </Text>
                        <Text size="textSm" weight="500">
                            {ec.user.fullName} {`(${ec.user.specialization})`}
                        </Text>
                    </DetailsField>

                    {!event.isGroupTreatment &&
                        event.category != 'team_meeting' &&
                        (defaultStatus == 'to_reschedule' || !!substituteWatch) && (
                            <GridItem spanColumn={2}>
                                {permissions?.agenda.write ? (
                                    <AgendaSubstituteField />
                                ) : (
                                    <DetailsField>
                                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                                            {t('agenda:substitute')}
                                        </Text>
                                        <Text size="textSm" weight="500">
                                            {substituteWatch?.fullName ?? '-'}
                                        </Text>
                                    </DetailsField>
                                )}
                            </GridItem>
                        )}
                </Grid>
            ))}
            {event.category == 'team_meeting' && (
                <>
                    <Divider direction="horizontal" />
                    <Grid columns={4} gap={4}>
                        {event.eventTeams.map((et, index) => (
                            <DetailsField key={et.id}>
                                <Text size="textSm" palette="neutral" shade="500" weight="500">
                                    {t('agenda:participant_x', {value: index + 1})}
                                </Text>
                                <Text size="textSm" weight="500">
                                    {et.member.fullName}
                                </Text>
                            </DetailsField>
                        ))}
                    </Grid>
                </>
            )}
            <Divider direction="horizontal" />
            <Grid columns={4} gap={4}>
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500" weight="500">
                        {t('commons:date')}
                    </Text>
                    <Text size="textSm" weight="500" style={{textTransform: 'capitalize'}}>
                        {formatLocaleDate(event.date, 'dddd D MMMM')}
                    </Text>
                </DetailsField>
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500" weight="500">
                        {t('agenda:start_time')}
                    </Text>
                    <Text size="textSm" weight="500">
                        {event.time
                            ? formatLocaleDate(`${formatDate(event.date, 'YYYY-MM-DD')} ${event.time}`, 'H:mm')
                            : '-'}
                    </Text>
                </DetailsField>
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500" weight="500">
                        {t('commons:duration')}
                    </Text>
                    <Text size="textSm" weight="500">
                        {t('commons:duration_minutes', {value: event.duration})}
                    </Text>
                </DetailsField>
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500" weight="500">
                        {t('agenda:room')}
                    </Text>
                    <Text size="textSm" weight="500">
                        {event.location && locationMapToLabel[event.location]
                            ? t(locationMapToLabel[event.location])
                            : '-'}
                    </Text>
                </DetailsField>
            </Grid>

            {event.frequency && event.position && event.dayOfWeek && (
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500">
                        {t('agenda:recurring_event')}
                    </Text>
                    <EventRepetitionFrequencyText
                        frequency={event.frequency}
                        position={event.position}
                        dayOfWeek={event.dayOfWeek}
                        date={event.date}
                        time={event.time || undefined}
                    />
                </DetailsField>
            )}

            <Flexbox direction="column" gap={4}>
                {!!substituteWatch && (
                    <DetailsField>
                        <Flexbox gap={2}>
                            <Text size="textSm" palette="neutral" shade="500" weight="500">
                                {t('agenda:patient_events')}
                            </Text>
                            <Text size="textSm" weight="500">
                                {t('agenda:x_managed_of_y', {
                                    count: event.eventPatients?.length,
                                    total: event.eventPatients?.length
                                })}
                            </Text>
                        </Flexbox>
                    </DetailsField>
                )}
                <DetailsField>
                    <Text size="textSm" palette="neutral" shade="500" weight="500">
                        {t('commons:notes')}
                    </Text>
                    <Text size="textSm" weight="500">
                        {event.note ?? '-'}
                    </Text>
                </DetailsField>
                {event.isRemote && (
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500" weight="500">
                            {t('accesses:remote')}
                        </Text>
                        {event.remoteUrl ? (
                            <ButtonLink
                                to={event.remoteUrl}
                                target="_blank"
                                shape="rectangle"
                                size="xs"
                                variant="secondary"
                            >
                                {t('commons:link')}
                            </ButtonLink>
                        ) : (
                            <Text size="textSm" weight="500">
                                {t('commons:yes')}
                            </Text>
                        )}
                    </DetailsField>
                )}

                {event.isARecovery && (
                    <DetailsField>
                        <Badge variant="neutral" size="sm">
                            {t('agenda:recovery_event')}
                        </Badge>
                    </DetailsField>
                )}
            </Flexbox>

            {/* Validation Error Display */}
            {firstValidationError && (
                <>
                    <Divider direction="horizontal" />
                    <Flexbox gap={2} align="center">
                        <Text
                            size="textSm"
                            palette="danger"
                            shade="700"
                            weight="400"
                            style={{display: 'flex', alignItems: 'center', gap: 4}}
                        >
                            <AlertCircleIcon style={{color: palette.danger[500]}} size={14} />
                            {firstValidationError}
                        </Text>
                    </Flexbox>
                </>
            )}
        </DialogBody>
    )
}
